import {
  CalendarIcon,
  CheckCircleIcon,
  DocumentTextIcon,
  FlagIcon,
  LinkIcon,
  PlusIcon,
} from '@heroicons/react/24/outline';
import axios from 'axios';
import { format } from 'date-fns';
import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useOrganization } from '../context/OrganizationContext';
import { getAuthHeaders } from '../utils/auth';
// Modal removed: using full-page editor instead
import NarrativeDisplay from '../components/NarrativeDisplay';
import { API_URL } from '../constants/urls';
interface OKRSummary {
  id: string;
  objective: string;
  description?: string;
  status: string;
  score?: number;
  planning_cycle_id?: string;
  planning_cycle_name?: string;
  owner_name?: string;
  is_corporate: boolean;
}

interface LinkedOKR {
  id: string;
  narrative_id: string;
  okr_id: string;
  relationship_type?: string;
  notes?: string;
  created_at: string;
  okr?: OKRSummary;
}

interface StrategicNarrativeData {
  id: string;
  organization_id: string;
  title: string;
  narrative_text: string;
  north_star?: string;
  timeframe_start?: string;
  timeframe_end?: string;
  status: string;
  version: number;
  parent_id?: string;
  created_at: string;
  updated_at: string;
  created_by?: string;
  updated_by?: string;
  linked_okrs: LinkedOKR[];
  okr_count: number;
  strategic_okr_count: number;
}

export default function StrategicNarrative() {
  const navigate = useNavigate();
  const location = useLocation();
  const { selectedOrg: organization, canEdit, isViewer } = useOrganization();
  const [narrative, setNarrative] = useState<StrategicNarrativeData | null>(
    null,
  );
  const [allNarratives, setAllNarratives] = useState<StrategicNarrativeData[]>(
    [],
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!organization) return;
    fetchNarratives();
  }, [organization, location.state]);

  const fetchNarratives = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch all narratives
      const allResponse = await axios.get(
        `${API_URL}/strategic-narratives?organization_id=${organization?.id}`,
        { headers: getAuthHeaders() },
      );
      setAllNarratives(allResponse.data);

      // Fetch active narrative
      try {
        const activeResponse = await axios.get(
          `${API_URL}/strategic-narratives/active?organization_id=${organization?.id}`,
          { headers: getAuthHeaders() },
        );
        setNarrative(activeResponse.data);
      } catch (err: any) {
        // It's okay if there's no active narrative
        if (err.response?.status !== 404) {
          console.error('Error fetching active narrative:', err);
        }
      }
    } catch (err: any) {
      console.error('Error fetching strategic narratives:', err);
      setError('Failed to load strategic narratives');
    } finally {
      setLoading(false);
    }
  };

  const fetchActiveNarrative = fetchNarratives;

  const handleCreateNarrative = () => {
    navigate(`/strategy/new?organization_id=${organization?.id || ''}`);
  };

  const handleEditNarrative = () => {
    if (narrative) {
      navigate(
        `/strategy/${narrative.id}/edit?organization_id=${organization?.id || ''}`,
      );
    }
  };

  const handleLinkOKRs = () => {
    // setShowLinkOKRsModal(true); // This state variable is removed
  };

  const getStatusBadgeColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-800';
      case 'draft':
        return 'bg-gray-100 text-gray-800';
      case 'superseded':
        return 'bg-yellow-100 text-yellow-800';
      case 'archived':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getRelationshipBadgeColor = (type?: string) => {
    switch (type) {
      case 'core':
        return 'bg-blue-100 text-blue-800';
      case 'supporting':
        return 'bg-purple-100 text-purple-800';
      case 'enabling':
        return 'bg-indigo-100 text-indigo-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-red-600">{error}</p>
      </div>
    );
  }

  // Blank state - no narrative exists at all
  if (!narrative && allNarratives.length === 0) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="text-center py-12">
          <FlagIcon className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-lg font-medium text-gray-900">
            No Strategic Narratives
          </h3>
          <p className="mt-1 text-sm text-gray-500">
            Start by creating your organization&apos;s strategic narrative to
            align your objectives and focus areas.
          </p>
          {canEdit && (
            <div className="mt-6">
              <button
                onClick={handleCreateNarrative}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                <PlusIcon className="-ml-1 mr-2 h-5 w-5" />
                Create Strategic Narrative
              </button>
            </div>
          )}
        </div>

        {/* Modal removed: use full-page editor */}
      </div>
    );
  }

  // Get active narrative and draft narratives
  const activeNarrative = allNarratives.find((n) => n.status === 'active');
  const draftNarratives = allNarratives.filter((n) => n.status === 'draft');
  const supersededNarratives = allNarratives.filter(
    (n) => n.status === 'superseded',
  );

  // Show active narrative prominently if it exists
  if (activeNarrative) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Active Narrative Section - Primary Focus */}
        <div className="mb-12">
          {/* Header with title and actions */}
          <div className="mb-8">
            <div className="flex justify-between items-start">
              <div>
                <div className="flex items-center gap-3 mb-2">
                  <h1 className="text-3xl font-bold text-gray-900">
                    {activeNarrative.title}
                  </h1>
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
                    <CheckCircleIcon className="h-4 w-4 mr-1" />
                    Active Strategy
                  </span>
                </div>
                <div className="flex items-center gap-4 text-sm text-gray-500">
                  <span className="flex items-center">
                    <CalendarIcon className="h-4 w-4 mr-1" />
                    {activeNarrative.timeframe_start &&
                    activeNarrative.timeframe_end
                      ? `${format(new Date(activeNarrative.timeframe_start), 'MMM d, yyyy')} - ${format(new Date(activeNarrative.timeframe_end), 'MMM d, yyyy')}`
                      : 'No timeframe set'}
                  </span>
                  <span>Version {activeNarrative.version || '-'}</span>
                  <span>
                    Updated{' '}
                    {format(
                      new Date(activeNarrative.updated_at),
                      'MMM d, yyyy',
                    )}
                  </span>
                </div>
              </div>
              {canEdit && (
                <div className="flex gap-2">
                  <button
                    onClick={() => {
                      // Navigate to new strategy page with current data pre-filled
                      navigate(
                        `/strategy/new?organization_id=${organization?.id}`,
                        {
                          state: {
                            copyFrom: {
                              title: activeNarrative.title,
                              narrative_text: activeNarrative.narrative_text,
                              north_star: activeNarrative.north_star,
                              timeframe_start: activeNarrative.timeframe_start,
                              timeframe_end: activeNarrative.timeframe_end,
                            },
                          },
                        },
                      );
                    }}
                    className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
                  >
                    <PlusIcon className="-ml-1 mr-2 h-5 w-5" />
                    Create New Version
                  </button>
                  <button
                    onClick={() =>
                      navigate(
                        `/strategy/${activeNarrative.id}/edit?organization_id=${organization?.id}`,
                      )
                    }
                    className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                  >
                    <DocumentTextIcon className="-ml-1 mr-2 h-5 w-5" />
                    View
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* North Star */}
          {activeNarrative.north_star && (
            <div className="mb-8 bg-blue-50 border-l-4 border-blue-400 p-6 rounded-r-lg">
              <div className="flex">
                <div className="flex-shrink-0">
                  <FlagIcon className="h-6 w-6 text-blue-400" />
                </div>
                <div className="ml-3">
                  <h3 className="text-lg font-semibold text-blue-900">
                    North Star Mission
                  </h3>
                  <div className="mt-2 text-base text-blue-800">
                    <p>{activeNarrative.north_star}</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Strategic Narrative Content */}
          <div className="bg-white shadow-lg rounded-lg overflow-hidden">
            <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-gray-900">
                Strategic Narrative
              </h2>
            </div>
            <div className="px-6 py-6">
              <NarrativeDisplay
                narrativeText={activeNarrative.narrative_text || ''}
                linkedOkrs={activeNarrative.linked_okrs || []}
              />
            </div>
            {activeNarrative.okr_count > 0 && (
              <div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
                <div className="flex items-center text-sm text-gray-600">
                  <LinkIcon className="h-4 w-4 mr-2" />
                  {activeNarrative.strategic_okr_count} strategic OKRs linked •{' '}
                  {activeNarrative.okr_count} total OKRs
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Draft Narratives Section - Secondary */}
        {draftNarratives.length > 0 && (
          <div className="border-t border-gray-200 pt-8">
            <div className="mb-4 flex justify-between items-center">
              <h2 className="text-lg font-medium text-gray-700">
                Draft Narratives
              </h2>
              {canEdit && (
                <button
                  onClick={handleCreateNarrative}
                  className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md text-blue-700 bg-blue-100 hover:bg-blue-200"
                >
                  <PlusIcon className="-ml-0.5 mr-1 h-4 w-4" />
                  New Draft
                </button>
              )}
            </div>
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {draftNarratives.map((narr) => (
                <div
                  key={narr.id}
                  className="bg-white rounded-lg shadow border border-gray-100 p-4"
                >
                  <div className="flex items-start justify-between mb-2">
                    <h3 className="text-sm font-medium text-gray-900 truncate flex-1">
                      {narr.title}
                    </h3>
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-50 text-blue-700 ml-2">
                      Draft
                    </span>
                  </div>
                  <p className="text-xs text-gray-500 mb-3">
                    {narr.version ? `Version ${narr.version}` : 'Draft'} •{' '}
                    {format(new Date(narr.created_at), 'MMM d, yyyy')}
                  </p>
                  <div className="flex gap-2">
                    <button
                      onClick={() => {
                        navigate(
                          `/strategy/${narr.id}/edit?organization_id=${organization?.id}`,
                        );
                      }}
                      className="text-xs text-blue-600 hover:text-blue-800"
                    >
                      Edit
                    </button>
                    {canEdit && (
                      <>
                        <button
                          onClick={async () => {
                            try {
                              await axios.put(
                                `${API_URL}/strategic-narratives/${narr.id}/activate?organization_id=${organization?.id}`,
                                {},
                                { headers: getAuthHeaders() },
                              );
                              fetchNarratives();
                            } catch (err) {
                              console.error('Error activating narrative:', err);
                            }
                          }}
                          className="text-xs text-green-600 hover:text-green-800"
                        >
                          Activate
                        </button>
                        <button
                          onClick={async () => {
                            if (
                              window.confirm(
                                `Delete "${narr.title}"? This erases history for this draft.`,
                              )
                            ) {
                              try {
                                await axios.delete(
                                  `${API_URL}/strategic-narratives/${narr.id}?organization_id=${organization?.id}`,
                                  { headers: getAuthHeaders() },
                                );
                                fetchNarratives();
                              } catch (err) {
                                console.error('Error deleting narrative:', err);
                              }
                            }
                          }}
                          className="text-xs text-red-600 hover:text-red-800"
                        >
                          Delete
                        </button>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* No drafts but allow creating new ones */}
        {draftNarratives.length === 0 && canEdit && (
          <div className="border-t border-gray-200 pt-8">
            <button
              onClick={handleCreateNarrative}
              className="inline-flex items-center px-4 py-2 border border-dashed border-gray-300 rounded-md text-sm font-medium text-gray-600 hover:text-gray-700 hover:border-gray-400"
            >
              <PlusIcon className="-ml-1 mr-2 h-5 w-5" />
              Create Draft Narrative
            </button>
          </div>
        )}

        {/* Past Versions (Superseded) */}
        {supersededNarratives.length > 0 && (
          <div className="border-t border-gray-200 pt-8">
            <div className="mb-4 flex justify-between items-center">
              <h2 className="text-lg font-medium text-gray-700">
                Past Versions
              </h2>
            </div>
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {supersededNarratives
                .sort((a, b) => (b.version || 0) - (a.version || 0))
                .map((narr) => (
                  <div
                    key={narr.id}
                    className="bg-gray-50 rounded-lg border border-gray-200 p-4"
                  >
                    <div className="flex items-start justify-between mb-2">
                      <h3 className="text-sm font-medium text-gray-900 truncate flex-1">
                        {narr.title}
                      </h3>
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-700 ml-2">
                        Superseded
                      </span>
                    </div>
                    <p className="text-xs text-gray-500 mb-3">
                      Version {narr.version} • Updated{' '}
                      {format(new Date(narr.updated_at), 'MMM d, yyyy')}
                    </p>
                    <div className="flex gap-2">
                      <button
                        onClick={() =>
                          navigate(
                            `/strategy/${narr.id}/edit?organization_id=${organization?.id}`,
                          )
                        }
                        className="text-xs text-blue-600 hover:text-blue-800"
                      >
                        View
                      </button>
                      {canEdit && (
                        <button
                          onClick={() => {
                            // Navigate to new strategy page with current data pre-filled
                            navigate(
                              `/strategy/new?organization_id=${organization?.id}`,
                              {
                                state: {
                                  copyFrom: {
                                    title: narr.title,
                                    narrative_text: narr.narrative_text,
                                    north_star: narr.north_star,
                                    timeframe_start: narr.timeframe_start,
                                    timeframe_end: narr.timeframe_end,
                                  },
                                },
                              },
                            );
                          }}
                          className="text-xs text-gray-700 hover:text-gray-900"
                        >
                          Create New Version
                        </button>
                      )}
                    </div>
                  </div>
                ))}
            </div>
          </div>
        )}

        {/* Modal removed: using full-page editor */}
      </div>
    );
  }

  // No active narrative - show drafts only
  if (allNarratives.length > 0) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-2xl font-semibold text-gray-900">
            Strategic Narratives
          </h1>
          <div className="mt-2 p-4 bg-amber-50 border border-amber-200 rounded-lg">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg
                  className="h-5 w-5 text-amber-400"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-amber-800">
                  No Active Strategy
                </h3>
                <p className="mt-1 text-sm text-amber-700">
                  Your organization doesn&apos;t have an active strategic
                  narrative. Review and activate one of the drafts below to set
                  your current strategy.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Draft Narratives */}
        <div className="mb-4 flex justify-between items-center">
          <h2 className="text-lg font-medium text-gray-900">
            Draft Narratives
          </h2>
          {canEdit && (
            <button
              onClick={handleCreateNarrative}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700"
            >
              <PlusIcon className="-ml-1 mr-2 h-5 w-5" />
              Create Narrative
            </button>
          )}
        </div>

        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {draftNarratives.map((narr) => (
            <div
              key={narr.id}
              className="bg-white rounded-lg shadow hover:shadow-lg transition-shadow p-6"
            >
              <div className="flex items-start justify-between mb-3">
                <h3 className="text-lg font-medium text-gray-900">
                  {narr.title}
                </h3>
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                  Draft
                </span>
              </div>

              <p className="text-sm text-gray-500 mb-3">
                {narr.version ? `Version ${narr.version}` : 'Draft'} • Created{' '}
                {format(new Date(narr.created_at), 'MMM d, yyyy')}
              </p>

              {narr.north_star && (
                <p className="text-sm text-gray-600 italic mb-4">
                  &ldquo;{narr.north_star}&rdquo;
                </p>
              )}

              <div className="flex gap-2">
                <button
                  onClick={() => {
                    navigate(
                      `/strategy/${narr.id}/edit?organization_id=${organization?.id}`,
                    );
                  }}
                  className="text-sm text-blue-600 hover:text-blue-800 font-medium"
                >
                  View
                </button>
                {canEdit && (
                  <>
                    <button
                      onClick={async () => {
                        try {
                          await axios.put(
                            `${API_URL}/strategic-narratives/${narr.id}/activate?organization_id=${organization?.id}`,
                            {},
                            { headers: getAuthHeaders() },
                          );
                          fetchNarratives();
                        } catch (err) {
                          console.error('Error activating narrative:', err);
                        }
                      }}
                      className="text-sm text-green-600 hover:text-green-800 font-medium"
                    >
                      Activate
                    </button>
                    {narr.status === 'draft' && (
                      <button
                        onClick={async () => {
                          if (
                            window.confirm(
                              `Are you sure you want to delete "${narr.title}"?`,
                            )
                          ) {
                            try {
                              await axios.delete(
                                `${API_URL}/strategic-narratives/${narr.id}?organization_id=${organization?.id}`,
                                { headers: getAuthHeaders() },
                              );
                              fetchNarratives();
                            } catch (err) {
                              console.error('Error deleting narrative:', err);
                            }
                          }
                        }}
                        className="text-sm text-red-600 hover:text-red-800"
                      >
                        Delete
                      </button>
                    )}
                  </>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Past Versions (Superseded) */}
        {supersededNarratives.length > 0 && (
          <div className="mt-10 border-t border-gray-200 pt-8">
            <h2 className="text-lg font-medium text-gray-900 mb-4">
              Past Versions
            </h2>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {supersededNarratives
                .sort((a, b) => (b.version || 0) - (a.version || 0))
                .map((narr) => (
                  <div
                    key={narr.id}
                    className="bg-gray-50 rounded-lg border border-gray-200 p-6"
                  >
                    <div className="flex items-start justify-between mb-3">
                      <h3 className="text-lg font-medium text-gray-900">
                        {narr.title}
                      </h3>
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                        Superseded
                      </span>
                    </div>
                    <p className="text-sm text-gray-500 mb-3">
                      Version {narr.version} • Updated{' '}
                      {format(new Date(narr.updated_at), 'MMM d, yyyy')}
                    </p>
                    <div className="flex gap-2">
                      <button
                        onClick={() =>
                          navigate(
                            `/strategy/${narr.id}/edit?organization_id=${organization?.id}`,
                          )
                        }
                        className="text-sm text-blue-600 hover:text-blue-800 font-medium"
                      >
                        View
                      </button>
                      {canEdit && (
                        <button
                          onClick={() => {
                            // Navigate to new strategy page with current data pre-filled
                            navigate(
                              `/strategy/new?organization_id=${organization?.id}`,
                              {
                                state: {
                                  copyFrom: {
                                    title: narr.title,
                                    narrative_text: narr.narrative_text,
                                    north_star: narr.north_star,
                                    timeframe_start: narr.timeframe_start,
                                    timeframe_end: narr.timeframe_end,
                                  },
                                },
                              },
                            );
                          }}
                          className="text-sm text-gray-700 hover:text-gray-900 font-medium"
                        >
                          Create New Version
                        </button>
                      )}
                    </div>
                  </div>
                ))}
            </div>
          </div>
        )}

        {/* Modal removed: using full-page editor */}
      </div>
    );
  }

  // This should never happen but TypeScript needs a return
  return null;
}
